import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Users, TrendingUp, Briefcase, ChevronRight, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const services = [
    {
      icon: <Globe className="w-8 h-8 text-blue-700" />,
      title: 'Market Entry Strategy',
      description: 'Comprehensive market analysis and entry strategies for Singapore, Asia, and global markets.'
    },
    {
      icon: <Users className="w-8 h-8 text-blue-700" />,
      title: 'Strategic Partnerships',
      description: 'Connecting businesses with distributors, industry experts, and ecosystem partners.'
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-blue-700" />,
      title: 'Business Development',
      description: 'Supporting cross-border growth and commercial expansion across international markets.'
    },
    {
      icon: <Briefcase className="w-8 h-8 text-blue-700" />,
      title: 'Capital Introduction',
      description: 'Introducing qualified investors and funding opportunities for growth-stage businesses.'
    },
    {
      icon: <Building2 className="w-8 h-8 text-blue-700" />,
      title: 'Private Market Opportunities',
      description: 'Access to selected private market opportunities through qualified partners and investment structures.'
    }
  ];

  const industries = [
    'Healthcare',
    'DeepTech',
    'Artificial Intelligence',
    'Energy',
    'Advanced Manufacturing'
  ];

  return (
    <div className="flex-1">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl fade-in">
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Helping Global Companies Expand Into
              <span className="text-blue-700"> Global Markets</span>
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              Aquavera helps international companies expand into Singapore, Asia, and global markets through strategic partnerships, market entry support, and capital introduction.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-blue-700 hover:bg-blue-800 text-white font-medium px-8 text-base">
                  Book a Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50 font-medium px-8 text-base">
                  Our Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gradient-to-b from-white to-slate-50 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Services</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive advisory services designed to accelerate your international growth
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card
                key={index}
                className="border-slate-200 hover:shadow-lg transition-shadow duration-300 cursor-pointer group"
              >
                <CardContent className="p-8">
                  <div className="mb-4 group-hover:scale-110 transition-transform duration-300">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{service.title}</h3>
                  <p className="text-slate-600 mb-4">{service.description}</p>
                  <Link
                    to="/services"
                    className="inline-flex items-center text-blue-700 font-medium hover:text-blue-800 transition-colors"
                  >
                    Learn more
                    <ChevronRight className="ml-1 w-4 h-4" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-24 bg-white px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Industries We Serve</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Deep expertise across high-growth sectors driving global innovation
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center hover:border-blue-700 hover:bg-blue-50 transition-all duration-300 cursor-pointer"
              >
                <p className="font-semibold text-slate-800">{industry}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-blue-700 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Expand Your Business Globally?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Let's discuss how Aquavera can accelerate your cross-border growth journey.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-white text-blue-700 hover:bg-slate-50 font-semibold px-8 text-base"
            >
              Schedule a Meeting
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
